/**
 * Bloco de insights holísticos gerado por Claude Sonnet 4.6.
 * Renderiza estado vazio (com CTA), loading, ou os insights formatados.
 */
import { Sparkles, Loader2, Star, AlertTriangle, Target, Quote } from "lucide-react";

export default function RodaInsights({ insights, loading, onGenerate }) {
  if (!insights) {
    return (
      <div
        className="bg-white rounded-2xl border border-[#E5E7EB] p-6 flex flex-col items-start gap-3 shadow-sm"
        data-testid="coach-insights-empty"
      >
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-[#4A5E2E]" />
          <h2 className="text-lg font-semibold text-[#0F1829]">Insights da tua Roda</h2>
        </div>
        <p className="text-sm text-[#374151]">
          Deixa o coach analisar a tua roda e revelar padrões cruzados que talvez
          não estejas a ver. Recebes um plano prático de 5 acções para as
          próximas 2 semanas.
        </p>
        <button
          onClick={onGenerate}
          disabled={loading}
          data-testid="coach-btn-generate-insights"
          className="inline-flex items-center gap-2 bg-[#4A5E2E] text-white text-sm font-medium px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading ? "A gerar insights…" : "Gerar insights com IA"}
        </button>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm"
      data-testid="coach-insights"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={20} className="text-[#4A5E2E]" />
        <h2 className="text-lg font-semibold text-[#0F1829]">
          Insights da tua Roda
        </h2>
        {typeof insights.overallScore === "number" && (
          <span
            className="ml-auto text-sm font-mono font-bold text-[#0F1829] bg-[#F3F4F6] px-2.5 py-1 rounded"
            data-testid="coach-insights-overall"
          >
            {insights.overallScore.toFixed(1)}/10
          </span>
        )}
      </div>

      {insights.resumo && (
        <p className="text-sm text-[#374151] leading-relaxed mb-4 whitespace-pre-line">
          {insights.resumo}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        {insights.areasFortes?.length > 0 && (
          <Panel titulo="Áreas fortes" icon={Star} accent="#4A5E2E">
            <ul className="text-sm space-y-1">
              {insights.areasFortes.map((a) => (
                <li key={a} className="flex gap-2">
                  <span className="text-[#4A5E2E]">▸</span>
                  <span className="text-[#374151]">{a}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
        {insights.areasCriticas?.length > 0 && (
          <Panel titulo="Áreas a trabalhar" icon={AlertTriangle} accent="#B91C1C">
            <ul className="text-sm space-y-1">
              {insights.areasCriticas.map((a) => (
                <li key={a} className="flex gap-2">
                  <span className="text-[#B91C1C]">▸</span>
                  <span className="text-[#374151]">{a}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>

      {insights.padroesCruzados?.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-[#0F1829] mb-2">Padrões cruzados</h3>
          <ul className="space-y-2">
            {insights.padroesCruzados.map((p, i) => (
              <li
                key={i}
                className="border-l-2 border-[#978D78] pl-3 py-1"
                data-testid={`coach-padrao-${i}`}
              >
                <div className="text-sm font-semibold text-[#0F1829]">{p.titulo}</div>
                <div className="text-sm text-[#374151]">{p.descricao}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {insights.proximosPassos?.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-[#0F1829] mb-2 flex items-center gap-1">
            <Target size={16} /> Plano das próximas 2 semanas
          </h3>
          <ol className="space-y-2 counter-reset-list">
            {insights.proximosPassos.map((p, i) => (
              <li
                key={i}
                className="flex gap-3 items-start"
                data-testid={`coach-passo-${i}`}
              >
                <span className="w-6 h-6 shrink-0 rounded-full bg-[#0F1829] text-white text-xs grid place-items-center font-semibold">
                  {i + 1}
                </span>
                <div>
                  <div className="text-sm font-semibold text-[#0F1829]">
                    {p.titulo}
                    {p.area && (
                      <span className="ml-2 text-xs font-normal text-[#6B7280]">
                        · {p.area}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-[#374151]">{p.acao}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {insights.fraseInspiradora && (
        <div className="mt-4 flex gap-3 items-start rounded-xl bg-[#FBFAF7] border border-[#978D78]/30 p-4">
          <Quote size={18} className="text-[#978D78] shrink-0 mt-0.5" />
          <p className="text-sm text-[#0F1829] italic">
            {insights.fraseInspiradora}
          </p>
        </div>
      )}
    </div>
  );
}

function Panel({ titulo, icon: Icon, accent, children }) {
  return (
    <div className="bg-[#FBFAF7] rounded-xl border border-[#E5E7EB] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color: accent }} />
        <div className="text-sm font-semibold text-[#0F1829]">{titulo}</div>
      </div>
      {children}
    </div>
  );
}
